import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useState,
} from "react";
import { TReportWithFields } from "../../../api/types/Report";
import { TListingColumn } from "../../../components/modalEditListingColumns";
import { ReportTableColumn } from "../../../components/reports/reportTable";
import { TCustomizableItemList } from "../../../components/types";

type TsetColumnsSearchKeyword = Dispatch<
  SetStateAction<Record<string, string>>
>;
type TsetColumnsOperator = Dispatch<SetStateAction<Record<string, string>>>;

const contextDefaultValue = {
  reportName: "",
  setReportName: (name: string) => {},
  selectedCategory: null as number | null,
  setSelectedCategory: (category: number) => {},
  selectedReportId: null as number | null,
  setSelectedReportId: (reportId: number) => {},
  selectedFields: [] as TCustomizableItemList[],
  setSelectedFields: (fields: TCustomizableItemList[]) => {},
  resetCreateCustomReportForms: () => {},
  columnsSearchKeyword: {} as Record<string, string>,
  setColumnsSearchKeyword: ((
    columnsSearchKeyword: Record<string, string>
  ) => {}) as TsetColumnsSearchKeyword,
  columnsOperator: {} as Record<string, string>,
  setColumnsOperator: ((
    columnsOperator: Record<string, string>
  ) => {}) as TsetColumnsOperator,
  specificReportResponse: undefined as TReportWithFields | undefined,
  setSpecificReportResponse: (report: TReportWithFields | undefined) => {},
  runReportParams: {} as any,
  setRunReportParams: (params: any) => {},
  skipRunReport: false,
  setSkipRunReport: (skip: boolean) => {},
  groupByColumns: [] as ReportTableColumn[],
  setGroupByColumns: (columns: ReportTableColumn[]) => {},
  subGroupByColumns: [] as ReportTableColumn[],
  setSubGroupByColumns: (columns: ReportTableColumn[]) => {},
  columnsVisibility: [] as TListingColumn[],
  setColumnsVisibility: (columns: TListingColumn[]) => {},
};

const createCustomReportContext = createContext(contextDefaultValue);

export const CreateCustomReportContextProvider: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [reportName, setReportName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [selectedFields, setSelectedFields] = useState<TCustomizableItemList[]>(
    []
  );

  // state to store specific report response
  const [specificReportResponse, setSpecificReportResponse] = useState<
    TReportWithFields | undefined
  >(undefined);

  // state to store run report API call params
  const [runReportParams, setRunReportParams] = useState<any>({});

  // state to skip run report API call
  const [skipRunReport, setSkipRunReport] = useState(false);

  // state to store search keyword for each column - to be used in run report API call
  const [columnsSearchKeyword, setColumnsSearchKeyword] = useState<
    Record<string, string>
  >({});

  // state to store operator for each column - to be used in run report API call
  const [columnsOperator, setColumnsOperator] = useState<
    Record<string, string>
  >({});

  const resetCreateCustomReportForms = () => {
    setReportName("");
    setSelectedCategory(null);
    setSelectedReportId(null);
    setSelectedFields([]);
  };

  // group by functionality
  const [groupByColumns, setGroupByColumns] = useState<ReportTableColumn[]>([]);
  const [subGroupByColumns, setSubGroupByColumns] = useState<
    ReportTableColumn[]
  >([]);

  // columns visibility
  const [columnsVisibility, setColumnsVisibility] = useState<TListingColumn[]>(
    []
  );

  return (
    <createCustomReportContext.Provider
      value={{
        reportName,
        setReportName,
        selectedCategory,
        setSelectedCategory,
        selectedReportId,
        setSelectedReportId,
        selectedFields,
        setSelectedFields,
        resetCreateCustomReportForms,
        columnsSearchKeyword,
        setColumnsSearchKeyword,
        columnsOperator,
        setColumnsOperator,
        specificReportResponse,
        setSpecificReportResponse,
        runReportParams,
        setRunReportParams,
        skipRunReport,
        setSkipRunReport,
        groupByColumns,
        setGroupByColumns,
        subGroupByColumns,
        setSubGroupByColumns,
        columnsVisibility,
        setColumnsVisibility,
      }}
    >
      {children}
    </createCustomReportContext.Provider>
  );
};

export const useCreateCustomReportContext = () => {
  const {
    reportName,
    setReportName,
    selectedCategory,
    setSelectedCategory,
    selectedReportId,
    setSelectedReportId,
    selectedFields,
    setSelectedFields,
    resetCreateCustomReportForms,
    columnsSearchKeyword,
    setColumnsSearchKeyword,
    columnsOperator,
    setColumnsOperator,
    specificReportResponse,
    setSpecificReportResponse,
    runReportParams,
    setRunReportParams,
    skipRunReport,
    setSkipRunReport,
    groupByColumns,
    setGroupByColumns,
    subGroupByColumns,
    setSubGroupByColumns,
    columnsVisibility,
    setColumnsVisibility,
  } = useContext(createCustomReportContext);
  return {
    reportName,
    setReportName,
    selectedCategory,
    setSelectedCategory,
    selectedReportId,
    setSelectedReportId,
    selectedFields,
    setSelectedFields,
    resetCreateCustomReportForms,
    columnsSearchKeyword,
    setColumnsSearchKeyword,
    columnsOperator,
    setColumnsOperator,
    specificReportResponse,
    setSpecificReportResponse,
    runReportParams,
    setRunReportParams,
    skipRunReport,
    setSkipRunReport,
    groupByColumns,
    setGroupByColumns,
    subGroupByColumns,
    setSubGroupByColumns,
    columnsVisibility,
    setColumnsVisibility,
  };
};
